import PropTypes from 'prop-types';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
// @mui
import { useTheme } from '@mui/material/styles';
import Stack from '@mui/material/Stack';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import MenuItem from '@mui/material/MenuItem';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import { LoadingButton } from '@mui/lab';
// theme
import { bgBlur } from 'src/theme/css';
// hooks
import { useOffSetTop } from 'src/hooks/use-off-set-top';
import { useResponsive } from 'src/hooks/use-responsive';
// components
import Logo from 'src/components/logo';
import SvgColor from 'src/components/svg-color';
import { useSettingsContext } from 'src/components/settings';
import Iconify from 'src/components/iconify';
// redux
import { useSelector } from 'react-redux';
// utils
import { isBirthdayToday } from 'src/utils/birthday';
import axiosInstance, { endpoints } from 'src/utils/axios';
//
import { HEADER, NAV } from '../config-layout';
import { Searchbar, AccountPopover, SettingsButton, NotificationsPopover } from '../_common';
import FestivalHeaderWidget from './common/FestivalHeaderWidget';
import BirthdayBanner from './common/BirthdayBanner';

// ----------------------------------------------------------------------

export default function Header({ onOpenNav, isOnboarding = false }) {
  const navigate = useNavigate();
  const theme = useTheme();

  const settings = useSettingsContext();

  const isNavHorizontal = settings.themeLayout === 'horizontal';

  const isNavMini = settings.themeLayout === 'mini';

  const lgUp = useResponsive('up', 'lg');

  const offset = useOffSetTop(HEADER.H_DESKTOP);

  const offsetTop = offset && !isNavHorizontal;

  // Get user from Redux store
  const user = useSelector((state) => state.auth.user);

  // Debug: Always log user data

  // Check if today is user's birthday
  const showBirthdayBanner = user?.dateOfBirth && isBirthdayToday(user.dateOfBirth);

  // Debug: Log user data to console
  // Debug: Log user data to console
  if (showBirthdayBanner) {
    // console.log('🎂 Birthday Banner - Showing for user:', user);
  }

  // Extract user name - try multiple fields
  const userName = user?.firstName || user?.name || user?.email?.split('@')[0] || 'User';

  const totalUnread = useSelector((s) => s.chat?.totalUnread || 0);

  // Active Task state & tracking logic
  const [activeTask, setActiveTask] = useState(null);
  const [, setTick] = useState(0);

  // Header pause dialog states
  const [openHeaderPause, setOpenHeaderPause] = useState(false);
  const [selectedPauseOption, setSelectedPauseOption] = useState('Short Break');
  const [customPauseText, setCustomPauseText] = useState('');
  const [headerPausing, setHeaderPausing] = useState(false);

  const fetchActiveTask = useCallback(async () => {
    try {
      const res = await axiosInstance.post(endpoints.myTasks.list, {
        status: 'in_progress',
        limit: 1,
      });
      if (res.data?.success && res.data.data?.length > 0) {
        setActiveTask(res.data.data[0]);
      } else {
        setActiveTask(null);
      }
    } catch (err) {
      console.error('Failed to fetch active task for header:', err);
    }
  }, []);

  const handleHeaderResume = async () => {
    try {
      const res = await axiosInstance.post(endpoints.myTasks.resume(activeTask._id));
      if (res.data?.success) {
        window.dispatchEvent(new Event('task-status-changed'));
      }
    } catch (err) {
      console.error('Failed to resume task from header:', err);
    }
  };

  const handleHeaderPauseSubmit = async () => {
    const finalReason = selectedPauseOption === 'Other' ? customPauseText.trim() : selectedPauseOption;
    if (!finalReason) return;
    try {
      setHeaderPausing(true);
      const res = await axiosInstance.post(endpoints.myTasks.pause(activeTask._id), {
        reason: finalReason,
      });
      if (res.data?.success) {
        setOpenHeaderPause(false);
        setSelectedPauseOption('Short Break');
        setCustomPauseText('');
        window.dispatchEvent(new Event('task-status-changed'));
      }
    } catch (err) {
      console.error('Failed to pause task from header:', err);
    } finally {
      setHeaderPausing(false);
    }
  };

  useEffect(() => {
    fetchActiveTask();
    
    // Listen for state changes (emitted when task is started, paused, resumed, or completed)
    window.addEventListener('task-status-changed', fetchActiveTask);
    
    return () => {
      window.removeEventListener('task-status-changed', fetchActiveTask);
    };
  }, [fetchActiveTask]);

  useEffect(() => {
    if (activeTask) {
      const interval = setInterval(() => {
        setTick((t) => t + 1);
      }, 1000);
      return () => clearInterval(interval);
    }
    return undefined;
  }, [activeTask]);

  const getHeaderTimer = () => {
    if (!activeTask?.startedAt) return { text: '', isDelayed: false };
    
    // Calculate total paused duration in milliseconds
    const totalPausedMs = (activeTask.pauses || []).reduce((acc, p) => {
      const end = p.resumedAt ? new Date(p.resumedAt) : new Date();
      return acc + (end - new Date(p.pausedAt));
    }, 0);

    const durationSecs = activeTask.duration * 60;
    
    // If paused, freeze countdown up to last paused moment
    const endPoint = activeTask.status === 'paused'
      ? new Date(activeTask.pauses[activeTask.pauses.length - 1].pausedAt)
      : new Date();
      
    const elapsedSecs = Math.floor((endPoint - new Date(activeTask.startedAt) - totalPausedMs) / 1000);
    const secsLeft = durationSecs - elapsedSecs;

    if (secsLeft <= 0) {
      const overSecs = Math.abs(secsLeft);
      const days = Math.floor(overSecs / 86400);
      const hours = Math.floor((overSecs % 86400) / 3600);
      const m = String(Math.floor((overSecs % 3600) / 60)).padStart(2, '0');
      const s = String(overSecs % 60).padStart(2, '0');
      const timeStr = `${String(hours).padStart(2, '0')}:${m}:${s}`;
      return {
        text: days > 0 ? `Overtime: ${days}d ${timeStr}` : `Overtime: ${timeStr}`,
        isDelayed: true,
      };
    }
    
    const days = Math.floor(secsLeft / 86400);
    const hours = Math.floor((secsLeft % 86400) / 3600);
    const m = String(Math.floor((secsLeft % 3600) / 60)).padStart(2, '0');
    const s = String(secsLeft % 60).padStart(2, '0');
    const timeStr = `${String(hours).padStart(2, '0')}:${m}:${s}`;
    return {
      text: days > 0 ? `${days}d ${timeStr}` : timeStr,
      isDelayed: false,
    };
  };

  const timerInfo = getHeaderTimer();

  let activeTaskColor = 'primary';
  if (activeTask?.status === 'paused') {
    activeTaskColor = 'warning';
  } else if (timerInfo.isDelayed) {
    activeTaskColor = 'error';
  }

  const renderActiveTaskWidget = activeTask && (
    <Tooltip title={`Active Task: ${activeTask.taskName}. Click to view.`} arrow>
      <Button
        onClick={() => navigate('/task_management/my_tasks')}
        variant="soft"
        color={activeTaskColor}
        size="small"
        startIcon={
          <Iconify
            icon="solar:alarm-play-bold"
            sx={{
              animation: activeTask.status === 'paused' ? 'none' : 'pulse 1.5s infinite',
              '@keyframes pulse': {
                '0%': { opacity: 0.6 },
                '50%': { opacity: 1 },
                '100%': { opacity: 0.6 },
              },
            }}
          />
        }
        sx={{
          mx: 1.5,
          fontWeight: 'bold',
          borderRadius: 1.5,
          textTransform: 'none',
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', display: { xs: 'none', sm: 'inline' } }}>
            {activeTask.taskName.length > 20 ? `${activeTask.taskName.slice(0, 20)}...` : activeTask.taskName}
          </Typography>
          
          <Typography
            variant="caption"
            sx={{
              fontFamily: 'monospace',
              fontWeight: 'bold',
              bgcolor: (t) => {
                if (activeTask.status === 'paused') return t.palette.warning.main;
                return timerInfo.isDelayed ? t.palette.error.main : t.palette.primary.main;
              },
              color: 'common.white',
              px: 1,
              py: 0.2,
              borderRadius: 0.5,
            }}
          >
            {activeTask.status === 'paused' ? 'PAUSED' : timerInfo.text}
          </Typography>

          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              if (activeTask.status === 'paused') {
                handleHeaderResume();
              } else {
                setSelectedPauseOption('Short Break');
                setCustomPauseText('');
                setOpenHeaderPause(true);
              }
            }}
            sx={{
              color: 'inherit',
              bgcolor: (t) => t.palette.action.hover,
              p: 0.3,
              borderRadius: 0.5,
            }}
          >
            <Iconify
              icon={activeTask.status === 'paused' ? 'solar:play-bold' : 'solar:pause-bold'}
              width={14}
              height={14}
            />
          </IconButton>
        </Stack>
      </Button>
    </Tooltip>
  );

  const renderPauseDialog = (
    <Dialog open={openHeaderPause} onClose={() => setOpenHeaderPause(false)} maxWidth="xs" fullWidth>
      <DialogTitle>Why are you pausing?</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            select
            label="Select Reason"
            value={selectedPauseOption}
            onChange={(e) => setSelectedPauseOption(e.target.value)}
            fullWidth
            required
          >
            <MenuItem value="Short Break">Short Break</MenuItem>
            <MenuItem value="Lunch / Tea Break">Lunch / Tea Break</MenuItem>
            <MenuItem value="Urgent Meeting / Call">Urgent Meeting / Call</MenuItem>
            <MenuItem value="Switching Tasks">Switching Tasks / Priority change</MenuItem>
            <MenuItem value="Other">Other (Please specify)</MenuItem>
          </TextField>

          {selectedPauseOption === 'Other' && (
            <TextField
              label="Specify Reason"
              placeholder="Why are you pausing?"
              value={customPauseText}
              onChange={(e) => setCustomPauseText(e.target.value)}
              fullWidth
              required
              multiline
              rows={2}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpenHeaderPause(false)}>Cancel</Button>
        <LoadingButton
          onClick={handleHeaderPauseSubmit}
          variant="contained"
          color="warning"
          loading={headerPausing}
          disabled={selectedPauseOption === 'Other' && !customPauseText.trim()}
        >
          Pause Task
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );

  const renderContent = (
    <>
      {lgUp && isNavHorizontal && <Logo sx={{ mr: 2.5 }} />}

      {!lgUp && !isOnboarding && (
        <IconButton onClick={onOpenNav}>
          <Badge color="error" variant="dot" invisible={totalUnread === 0}>
            <SvgColor src="/assets/icons/navbar/ic_menu_item.svg" />
          </Badge>
        </IconButton>
      )}

      {!isOnboarding && <Searchbar />}

      <Stack
        flexGrow={1}
        direction="row"
        alignItems="center"
        justifyContent="flex-end"
        spacing={{ xs: 0.5, sm: 1 }}
      >
        {renderActiveTaskWidget}

        <FestivalHeaderWidget />

        {/* Birthday Badge - shown next to notifications */}
        {showBirthdayBanner && <BirthdayBanner userName={userName} />}

        <NotificationsPopover />

        <SettingsButton />

        <AccountPopover />
      </Stack>
    </>
  );

  return (
    <Box>

      {/* Main Header */}
      <AppBar
        sx={{
          height: HEADER.H_MOBILE,
          zIndex: theme.zIndex.appBar + 1,
          ...bgBlur({
            color: theme.palette.background.default,
          }),
          transition: theme.transitions.create(['height'], {
            duration: theme.transitions.duration.shorter,
          }),
          ...(lgUp && {
            width: `calc(100% - ${NAV.W_VERTICAL + 1}px)`,
            height: HEADER.H_DESKTOP,
            ...(offsetTop && {
              height: HEADER.H_DESKTOP_OFFSET,
            }),
            ...(isNavHorizontal && {
              width: 1,
              bgcolor: 'background.default',
              height: HEADER.H_DESKTOP_OFFSET,
              borderBottom: `dashed 1px ${theme.palette.divider}`,
            }),
            ...(isNavMini && {
              width: `calc(100% - ${NAV.W_MINI + 1}px)`,
            }),
            // Onboarding: full-width header
            ...(isOnboarding && {
              width: '100%',
            }),
          }),
        }}
      >
        <Toolbar
          sx={{
            height: 1,
            px: { lg: 5 },
          }}
        >
          {renderContent}
        </Toolbar>
      </AppBar>
      {renderPauseDialog}
    </Box>
  );
}

Header.propTypes = {
  onOpenNav: PropTypes.func,
  isOnboarding: PropTypes.bool,
};
